import { COST_CATEGORIES, type AllocationKey, type CostCategory } from './enums';
import { DEFAULT_KEYS, RULES } from './rules';

/**
 * Umlage-Assistent ("Was kannst du umlegen?"): which operating costs a landlord may pass on to tenants (§ 2 BetrKV,
 * § 556 BGB), with plain examples, conditions and the default split, plus the status of each cost type for one property
 * and year. Pure data and logic; the texts live in @immoaktas/i18n under `common:umlage` (keys named here).
 * Informational only: no individual legal advice.
 */

/** Numbered conditions of a cost type; text key `common:umlage.condition.<id>`. */
export const UMLAGE_CONDITIONS = [
  'leaseNamed',
  'heizkostenv',
  'co2',
  'noRepair',
  'caretakerShare',
  'cableTv',
  'antennaPower',
  'ownProtection',
  'recurring',
  'noNewPlanting',
  'noOneOff',
  'sharedOnly',
  'meters',
  'rainwater',
  'ownWork',
] as const;
export type UmlageCondition = (typeof UMLAGE_CONDITIONS)[number];

/** Typical receipts of a cost type; text key `common:umlage.example.<id>`. */
export const UMLAGE_EXAMPLES = [
  'propertyTaxNotice',
  'waterBill',
  'waterMeterRent',
  'waterMeterCalibration',
  'sewageFee',
  'rainwaterFee',
  'fuel',
  'heatingService',
  'meteringService',
  'heatingPower',
  'hotWaterHeating',
  'legionella',
  'elevatorPower',
  'elevatorService',
  'elevatorEmergency',
  'streetCleaningFee',
  'winterService',
  'wasteFee',
  'bulkyWaste',
  'stairwellCleaning',
  'windowCleaning',
  'pestControl',
  'lawnMowing',
  'hedgeTrimming',
  'playgroundSand',
  'stairwellLight',
  'cellarLight',
  'outdoorLight',
  'chimneySweep',
  'emissionCheck',
  'buildingInsurance',
  'liabilityInsurance',
  'glassInsurance',
  'caretakerService',
  'antennaService',
  'distributionPower',
  'washingMachine',
  'smokeDetectorService',
  'gutterCleaning',
  'fireExtinguisherService',
] as const;
export type UmlageExample = (typeof UMLAGE_EXAMPLES)[number];

export type UmlageEntry = {
  category: CostCategory;
  /** Number in § 2 BetrKV (Nr. 4 to 6 are heating and hot water). */
  betrKvNo: number;
  examples: readonly UmlageExample[];
  conditions: readonly UmlageCondition[];
  /** Often missing from statements of private landlords: shown in "Oft vergessen" when not tracked yet. */
  oftenForgotten: boolean;
};

/** One entry per cost type, in COST_CATEGORIES order (= § 2 BetrKV order). */
export const UMLAGE_CATALOGUE: readonly UmlageEntry[] = [
  {
    category: 'property_tax',
    betrKvNo: 1,
    examples: ['propertyTaxNotice'],
    conditions: [],
    oftenForgotten: false,
  },
  {
    category: 'water',
    betrKvNo: 2,
    examples: ['waterBill', 'waterMeterRent', 'waterMeterCalibration'],
    conditions: ['meters'],
    oftenForgotten: false,
  },
  {
    category: 'sewage',
    betrKvNo: 3,
    examples: ['sewageFee', 'rainwaterFee'],
    conditions: ['rainwater'],
    oftenForgotten: true,
  },
  {
    category: 'heating',
    betrKvNo: 4,
    examples: ['fuel', 'heatingService', 'meteringService', 'heatingPower'],
    conditions: ['heizkostenv', 'co2', 'noRepair'],
    oftenForgotten: false,
  },
  {
    category: 'hot_water',
    betrKvNo: 5,
    examples: ['hotWaterHeating', 'meteringService'],
    conditions: ['heizkostenv', 'noRepair'],
    oftenForgotten: false,
  },
  {
    category: 'elevator',
    betrKvNo: 7,
    examples: ['elevatorPower', 'elevatorService', 'elevatorEmergency'],
    conditions: ['noRepair'],
    oftenForgotten: false,
  },
  {
    category: 'street_cleaning',
    betrKvNo: 8,
    examples: ['streetCleaningFee', 'winterService'],
    conditions: ['ownWork'],
    oftenForgotten: true,
  },
  {
    category: 'waste',
    betrKvNo: 8,
    examples: ['wasteFee', 'bulkyWaste'],
    conditions: [],
    oftenForgotten: false,
  },
  {
    category: 'building_cleaning',
    betrKvNo: 9,
    examples: ['stairwellCleaning', 'windowCleaning'],
    conditions: ['noOneOff', 'ownWork'],
    oftenForgotten: false,
  },
  {
    category: 'pest_control',
    betrKvNo: 9,
    examples: ['pestControl'],
    conditions: ['recurring'],
    oftenForgotten: false,
  },
  {
    category: 'garden',
    betrKvNo: 10,
    examples: ['lawnMowing', 'hedgeTrimming', 'playgroundSand'],
    conditions: ['noNewPlanting', 'ownWork'],
    oftenForgotten: false,
  },
  {
    category: 'common_electricity',
    betrKvNo: 11,
    examples: ['stairwellLight', 'cellarLight', 'outdoorLight'],
    conditions: [],
    oftenForgotten: true,
  },
  {
    category: 'chimney',
    betrKvNo: 12,
    examples: ['chimneySweep', 'emissionCheck'],
    conditions: [],
    oftenForgotten: true,
  },
  {
    category: 'insurance',
    betrKvNo: 13,
    examples: ['buildingInsurance', 'liabilityInsurance', 'glassInsurance'],
    conditions: ['ownProtection'],
    oftenForgotten: true,
  },
  {
    category: 'caretaker',
    betrKvNo: 14,
    examples: ['caretakerService'],
    conditions: ['caretakerShare'],
    oftenForgotten: false,
  },
  {
    category: 'antenna_cable',
    betrKvNo: 15,
    examples: ['antennaService', 'distributionPower'],
    conditions: ['cableTv', 'antennaPower'],
    oftenForgotten: false,
  },
  {
    category: 'laundry',
    betrKvNo: 16,
    examples: ['washingMachine'],
    conditions: ['sharedOnly'],
    oftenForgotten: false,
  },
  {
    category: 'other_operating',
    betrKvNo: 17,
    examples: ['smokeDetectorService', 'gutterCleaning', 'legionella', 'fireExtinguisherService'],
    conditions: ['leaseNamed'],
    oftenForgotten: true,
  },
];

const BY_CATEGORY = new Map(UMLAGE_CATALOGUE.map((e) => [e.category, e]));
export const umlageEntry = (c: CostCategory): UmlageEntry => BY_CATEGORY.get(c)!;

/** § 2 BetrKV number of a cost type ("umlagefähig · BetrKV Nr. 13"). */
export const BETRKV_NO = Object.fromEntries(UMLAGE_CATALOGUE.map((e) => [e.category, e.betrKvNo])) as Record<
  CostCategory,
  number
>;

/** Costs the landlord always pays himself; text keys `common:umlage.not.<id>.title|why`. */
export const NOT_APPORTIONABLE = [
  'administration',
  'repairs',
  'reserve',
  'bankFees',
  'vacancy',
  'legal',
  'hoaShares',
  'landlordInsurance',
  'purchases',
] as const;
export type NotApportionable = (typeof NOT_APPORTIONABLE)[number];

/** Split key the statement uses for a cost type: property setting, else account default, else the legal default. */
export function effectiveKey(
  c: CostCategory,
  propertyKeys?: Partial<Record<CostCategory, AllocationKey>> | null,
  accountKeys?: Partial<Record<CostCategory, AllocationKey>> | null,
): AllocationKey {
  return propertyKeys?.[c] ?? accountKeys?.[c] ?? DEFAULT_KEYS[c];
}

/** Cable TV fees are not apportionable for service days from 01.07.2024 (TKMoG); true when the year reaches that day. */
export const cableTvExcluded = (year: number) => `${year}-12-31` >= RULES.cableTvNotApportionableFrom;

// ───────────── status per property and year ─────────────

/** One cost row of the year file (GET /properties/:id/year-file), structurally. */
export type UmlageYearRow = {
  category: string;
  /** complete | missing | expected_later | review | not_applicable */
  status: string;
  amountCents: number;
  costItemIds: readonly string[];
  /** 12 months: covered | manual | review | missing | expected | na */
  months: readonly string[];
};
/** A receipt that waits for review, with the cost types it is (probably) for. */
export type UmlagePendingDoc = { id: string; categories: readonly string[] };

/**
 * - recorded: costs entered for the year (receipt or manual), the year is covered
 * - partial: costs entered, but months of the year are still missing (e.g. insurance for 9 of 12 months)
 * - review: a receipt for this cost type waits for review
 * - missing: expected (property profile or last year) and nothing entered yet
 * - later: expected, but the year is still running and nothing is due yet
 * - absent: the landlord said "haben wir nicht" for this property
 * - open: not tracked for this property (could still apply)
 */
export type UmlageState = 'recorded' | 'partial' | 'review' | 'missing' | 'later' | 'absent' | 'open';

export type UmlageStatus = {
  category: CostCategory;
  state: UmlageState;
  /** Tracked for the property: expected by profile or last year, or costs exist. */
  expected: boolean;
  /** Apportionable sum entered for the year (cents, periodized to the year). */
  amountCents: number;
  /** Cost positions entered for the year. */
  count: number;
  /** Months of the year still without costs (partial, missing). */
  missingMonths: number;
  /** Receipts for this cost type waiting for review. */
  pendingDocumentIds: string[];
  /**
   * The year file still counts this cost type as missing: a receipt for it waits for review, but no costs are entered
   * yet. Such rows stay under "Fehlt noch", so the assistant shows the same count as the billing readiness
   * ("3 von 12 Kostenarten fehlen noch").
   */
  stillMissing: boolean;
};

export function umlageStatus(
  rows: readonly UmlageYearRow[],
  opts: { absent?: readonly string[]; pending?: readonly UmlagePendingDoc[] } = {},
): UmlageStatus[] {
  const absent = new Set(opts.absent ?? []);
  return COST_CATEGORIES.map((category) => {
    const row = rows.find((r) => r.category === category);
    const pendingDocumentIds = (opts.pending ?? [])
      .filter((d) => d.categories.includes(category))
      .map((d) => d.id);
    const count = row?.costItemIds.length ?? 0;
    const missingMonths = row ? row.months.filter((m) => m === 'missing').length : 0;
    const amountCents = row?.amountCents ?? 0;
    const tracked = !!row && row.status !== 'not_applicable';
    const base = {
      category,
      expected: tracked || count > 0,
      amountCents,
      count,
      missingMonths,
      pendingDocumentIds,
      stillMissing: count === 0 && row?.status === 'missing',
    };
    const done = count > 0 && (row?.status === 'complete' || row?.status === 'expected_later');
    if (done) return { ...base, state: 'recorded' as const };
    if (pendingDocumentIds.length || row?.status === 'review') return { ...base, state: 'review' as const };
    if (count > 0)
      return { ...base, state: row?.status === 'missing' ? ('partial' as const) : ('recorded' as const) };
    if (absent.has(category)) return { ...base, expected: false, state: 'absent' as const };
    if (row?.status === 'missing') return { ...base, state: 'missing' as const };
    if (row?.status === 'expected_later') return { ...base, state: 'later' as const };
    return { ...base, expected: false, state: 'open' as const };
  });
}

export type UmlageSections = {
  /** Fehlt noch: missing, partial, later and receipts waiting for review of a still missing type (urgent first). */
  missing: UmlageStatus[];
  /** Schon erfasst: recorded and receipts waiting for review of entered costs. */
  recorded: UmlageStatus[];
  /** Oft vergessen: not tracked yet, often forgotten. */
  forgotten: UmlageStatus[];
  /** Weitere Kostenarten: not tracked, rarely relevant. */
  more: UmlageStatus[];
  /** Haben wir nicht. */
  absent: UmlageStatus[];
};

const URGENCY: Record<UmlageState, number> = {
  missing: 0,
  partial: 1,
  later: 2,
  review: 0,
  recorded: 1,
  absent: 0,
  open: 0,
};

export function umlageSections(list: readonly UmlageStatus[]): UmlageSections {
  const by = (states: UmlageState[], keep: (s: UmlageStatus) => boolean = () => true) =>
    list
      .filter((s) => states.includes(s.state) && keep(s))
      .sort((a, b) => URGENCY[a.state] - URGENCY[b.state]);
  const open = by(['open']);
  // a receipt waits for a type the year file still counts as missing: stays under "Fehlt noch"
  const waitingMissing = (s: UmlageStatus) => s.state === 'review' && s.stillMissing;
  return {
    missing: by(['missing', 'partial', 'later', 'review'], (s) => s.state !== 'review' || waitingMissing(s)),
    recorded: by(['review', 'recorded'], (s) => !waitingMissing(s)),
    forgotten: open.filter((s) => umlageEntry(s.category).oftenForgotten),
    more: open.filter((s) => !umlageEntry(s.category).oftenForgotten),
    absent: by(['absent']),
  };
}

/**
 * New expected/absent lists of a property after "Haben wir nicht" (`absent: true`) or "Wieder aufnehmen". Both lists
 * stay sorted in catalogue order and free of duplicates; the server stores them via PATCH /properties/:id.
 */
export function toggleAbsent(
  p: {
    expectedCostCategories: readonly CostCategory[];
    absentCostCategories?: readonly CostCategory[] | null;
  },
  c: CostCategory,
  absent: boolean,
): { expectedCostCategories: CostCategory[]; absentCostCategories: CostCategory[] } {
  const exp = new Set(p.expectedCostCategories);
  const abs = new Set(p.absentCostCategories ?? []);
  if (absent) {
    exp.delete(c);
    abs.add(c);
  } else {
    abs.delete(c);
    exp.add(c);
  }
  const order = (s: Set<CostCategory>) => COST_CATEGORIES.filter((x) => s.has(x));
  return { expectedCostCategories: order(exp), absentCostCategories: order(abs) };
}

/** Cost types a receipt waiting for review is probably for: the AI's line categories, else the upload hint. */
export function pendingCategories(d: {
  fields?: { lines?: unknown } | null;
  uploadHint?: { category?: string | null } | null;
}): string[] {
  const lines = Array.isArray(d.fields?.lines) ? (d.fields!.lines as { suggestedCategory?: unknown }[]) : [];
  const cats = lines
    .map((l) => l.suggestedCategory)
    .filter((x): x is string => typeof x === 'string' && (COST_CATEGORIES as readonly string[]).includes(x));
  if (cats.length) return [...new Set(cats)];
  return d.uploadHint?.category ? [d.uploadHint.category] : [];
}

/** Heating types with a building-wide heating bill (central boiler, district heating): heating costs are expected. */
export const CENTRAL_HEATING_TYPES: readonly string[] = ['central_gas', 'central_oil', 'district'];

/**
 * Cost types the completeness check expects for a year (docs/01 Vollständigkeits-Check): the property profile plus
 * everything last year had, plus heating for a central or district heating, minus what the landlord marked
 * "haben wir nicht". Hot water is not derived (the property does not record whether hot water is central); it is
 * expected once it was in the profile or last year.
 */
export function expectedCategories(
  p: { expectedCostCategories: readonly string[]; absentCostCategories?: readonly string[] | null; heating?: { type?: string | null } | null },
  previousYear: readonly string[],
): Set<string> {
  const absent = new Set(p.absentCostCategories ?? []);
  const heating = p.heating?.type && CENTRAL_HEATING_TYPES.includes(p.heating.type) ? ['heating'] : [];
  return new Set([...p.expectedCostCategories, ...previousYear, ...heating].filter((c) => !absent.has(c)));
}

/**
 * Service period the assistant suggests for a cost type of `year` (upload hint, manual amount): the property's billing
 * period starting in that year, else one overlapping it, else the calendar year.
 */
export function umlageWindow(
  periods: readonly { id: string; propertyId: string; from: string; to: string }[],
  propertyId: string,
  year: number,
): { from: string; to: string; periodId?: string } {
  const own = periods.filter(
    (p) => p.propertyId === propertyId && p.from <= `${year}-12-31` && p.to >= `${year}-01-01`,
  );
  const p = own.find((x) => x.from.slice(0, 4) === String(year)) ?? own[0];
  return p ? { from: p.from, to: p.to, periodId: p.id } : { from: `${year}-01-01`, to: `${year}-12-31` };
}
